type GradeOverride = {
  assignmentId: string
  userId: string
  roomId: string
  overrideTotalScore: number
  appealOverride: boolean
}

export type GradeOverrideMap = ReadonlyMap<string, GradeOverride>

type GradeOverrideLookup = {
  overrides: GradeOverrideMap
  assignmentId: string
  userId: string
  roomId: string
}

type EffectiveFinalGradeSortEntry = {
  userId: string
  roomId: string
  calculatedTotalScore: number | undefined
  roomIsEmpty: boolean
}

function gradeOverrideKey(
  assignmentId: string,
  userId: string,
  roomId: string
) {
  return JSON.stringify([assignmentId, userId, roomId])
}

export function createGradeOverrideMap(
  overrides: readonly GradeOverride[]
): GradeOverrideMap {
  return new Map(
    overrides.map((override) => [
      gradeOverrideKey(override.assignmentId, override.userId, override.roomId),
      override,
    ])
  )
}

export function getGradeOverride(
  overrides: GradeOverrideMap,
  assignmentId: string,
  userId: string,
  roomId: string
) {
  return overrides.get(gradeOverrideKey(assignmentId, userId, roomId))
    ?.overrideTotalScore
}

export function isAppealOverride({
  overrides,
  assignmentId,
  userId,
  roomId,
}: GradeOverrideLookup) {
  return (
    overrides.get(gradeOverrideKey(assignmentId, userId, roomId))
      ?.appealOverride === true
  )
}

export function compareEffectiveFinalGrades({
  first,
  second,
  overrides,
  assignmentId,
  order,
}: {
  first: EffectiveFinalGradeSortEntry
  second: EffectiveFinalGradeSortEntry
  overrides: GradeOverrideMap
  assignmentId: string
  order: 'asc' | 'desc'
}) {
  if (first.roomIsEmpty || second.roomIsEmpty) {
    if (!first.roomIsEmpty) return -1
    if (!second.roomIsEmpty) return 1
    return 0
  }

  const firstScore =
    getGradeOverride(overrides, assignmentId, first.userId, first.roomId) ??
    first.calculatedTotalScore ??
    0
  const secondScore =
    getGradeOverride(overrides, assignmentId, second.userId, second.roomId) ??
    second.calculatedTotalScore ??
    0
  const comparison = firstScore - secondScore

  return order === 'asc' ? comparison : -comparison
}
